import React, { useEffect, useState } from "react";
import { Flex, Button, Text, Input, Switch, toast } from "@appsmith/ads";
import { createMessage, IN_APP_EMBED_SETTING } from "ee/constants/messages";
import { getAgentChatQueries } from "ee/selectors/aiAgentSelectors";
import { useSelector } from "react-redux";
import { getSnippetUrl } from "ee/utils";
import useUpdateEmbedSnippet from "pages/Applications/EmbedSnippet/useUpdateEmbedSnippet";
import { getCurrentPageId } from "selectors/editorSelectors";
import { uniqBy } from "lodash";

const CHROME_EXTENSION_URL =
  "https://chromewebstore.google.com/detail/appsmith-agents/mlmnoikcaeggnjijgimjbiojaeeeifal";

interface URLMapping {
  assistantUrl: string;
  websiteUrl: string;
}

interface ChromeExtensionConfig {
  extensionId?: string;
  defaultAssistantUrl?: string;
  urlMappings?: URLMapping[];
}

export function ChromeExtensionBanner() {
  const agentsQueries = useSelector(getAgentChatQueries);
  const isAgentsApp = agentsQueries.length > 0;
  const [chromeExtensionConfig, setChromeExtensionConfig] =
    useState<ChromeExtensionConfig>({
      urlMappings: [],
    });
  const embedSnippet = useUpdateEmbedSnippet();
  const embedURL = getSnippetUrl(embedSnippet.appViewEndPoint, true, "none");
  const pageId = useSelector(getCurrentPageId);

  useEffect(function checkChromeExtensionInstallation() {
    window.postMessage({ type: "PING_APPSMITH_AGENT_EXTENSION" });
  }, []);

  useEffect(
    function setupMessageListener() {
      function handleMessage(event: MessageEvent) {
        if (event.data.type === "PONG_APPSMITH_AGENT_EXTENSION") {
          // extension is installed or enabled
          if (event.data.extensionId) {
            setChromeExtensionConfig({
              ...event.data,
              defaultAssistantUrl:
                chromeExtensionConfig.defaultAssistantUrl !== undefined
                  ? chromeExtensionConfig.defaultAssistantUrl
                  : event.data.defaultAssistantUrl,
              urlMappings: uniqBy(
                [
                  ...(event.data.urlMappings || []),
                  ...(chromeExtensionConfig?.urlMappings || []),
                ],
                "websiteUrl",
              ),
            });

            return;
          }

          // extension is disabled or uninstalled
          setChromeExtensionConfig({});
        }
      }

      window.addEventListener("message", handleMessage);

      return () => {
        window.removeEventListener("message", handleMessage);
      };
    },
    [chromeExtensionConfig],
  );

  const handleAddUrlMapping = () => {
    setChromeExtensionConfig({
      ...chromeExtensionConfig,
      urlMappings: [
        ...(chromeExtensionConfig?.urlMappings || []),
        { websiteUrl: "", assistantUrl: embedURL },
      ],
    });
  };

  const handleUpdateUrlMapping = (
    index: number,
    field: keyof URLMapping,
    value: string,
  ) => {
    const newMappings = [...(chromeExtensionConfig?.urlMappings || [])];

    newMappings[index] = {
      ...newMappings[index],
      [field]: value,
    };

    setChromeExtensionConfig({
      ...chromeExtensionConfig,
      urlMappings: newMappings,
    });
  };

  const handleRemoveUrlMapping = (index: number) => {
    const newMappings = (chromeExtensionConfig?.urlMappings || []).filter(
      (_, i) => i !== index,
    );

    setChromeExtensionConfig({
      ...chromeExtensionConfig,
      urlMappings: newMappings,
    });
  };

  const onChangeDefaultAssistantUrl = (isSelected: boolean) => {
    setChromeExtensionConfig({
      ...chromeExtensionConfig,
      defaultAssistantUrl: isSelected ? embedURL : "",
    });
  };

  const handleSaveUrlMappings = () => {
    postMessage({
      type: "SAVE_APPSMITH_AGENTS_SETTINGS",
      urlMappings: chromeExtensionConfig.urlMappings,
      defaultAssistantUrl: chromeExtensionConfig.defaultAssistantUrl,
    });

    toast.show("Settings saved successfully", {
      kind: "success",
    });
  };

  if (!isAgentsApp) return null;

  return (
    <Flex
      borderTop="1px solid var(--ads-v2-color-border)"
      flexDirection="column"
      gap="spaces-2"
      paddingTop="spaces-5"
    >
      {chromeExtensionConfig.extensionId ? (
        <>
          <Flex
            alignItems="center"
            justifyContent="space-between"
            paddingBottom="spaces-3"
          >
            <Text kind="heading-s">
              {createMessage(
                IN_APP_EMBED_SETTING.chromeExtensionBannerTitle,
                true,
              )}
            </Text>
            <Button
              endIcon="share-box-line"
              href={
                chromeExtensionConfig.extensionId
                  ? `chrome-extension://${chromeExtensionConfig.extensionId}/src/options/options.html`
                  : CHROME_EXTENSION_URL
              }
              kind="tertiary"
              onClick={(e) => {
                if (chromeExtensionConfig.extensionId) {
                  e.preventDefault();

                  postMessage({
                    type: "OPEN_APPSMITH_AGENTS_EXTENSION",
                  });
                }
              }}
              renderAs="a"
              target="_blank"
            >
              {createMessage(
                IN_APP_EMBED_SETTING.chromeExtensionBannerButton,
                Boolean(chromeExtensionConfig.extensionId),
              )}
            </Button>
          </Flex>
          <Flex flexDirection="column" gap="spaces-4">
            <Switch
              isSelected={(() => {
                try {
                  const defaultAssistantUrl =
                    chromeExtensionConfig.defaultAssistantUrl;

                  if (!defaultAssistantUrl) return false;

                  const hasSamePageId = defaultAssistantUrl.includes(pageId);
                  const assistantUrlDomain = new URL(defaultAssistantUrl)
                    .hostname;
                  const embedUrlDomain = new URL(embedURL).hostname;
                  const hasSameDomain = embedUrlDomain === assistantUrlDomain;

                  return hasSamePageId && hasSameDomain;
                } catch (err) {
                  return false;
                }
              })()}
              onChange={onChangeDefaultAssistantUrl}
            >
              Make this Agent as default
            </Switch>
            <Flex alignItems="start" flexDirection="column" gap="spaces-3">
              <Text kind="body-m">Website URLs</Text>
              {(chromeExtensionConfig.urlMappings || []).map(
                (mapping, index) => {
                  if (mapping.assistantUrl) {
                    try {
                      const assistantUrl = mapping.assistantUrl;

                      const hasSamePageId = assistantUrl.includes(pageId);
                      const assistantUrlDomain = new URL(assistantUrl).hostname;
                      const embedUrlDomain = new URL(embedURL).hostname;
                      const hasSameDomain =
                        embedUrlDomain === assistantUrlDomain;

                      if (!(hasSamePageId && hasSameDomain)) {
                        return false;
                      }
                    } catch (err) {
                      return false;
                    }
                  }

                  return (
                    <Flex
                      alignItems="flex-end"
                      gap="spaces-3"
                      key={index}
                      width="100%"
                    >
                      <Input
                        onChange={(value) =>
                          handleUpdateUrlMapping(index, "websiteUrl", value)
                        }
                        placeholder="Enter website URL"
                        size="md"
                        type="url"
                        value={mapping.websiteUrl}
                      />
                      <Button
                        endIcon="delete-bin-line"
                        isIconButton
                        kind="tertiary"
                        onClick={() => handleRemoveUrlMapping(index)}
                        size="md"
                        style={{ minWidth: 36 }}
                      />
                    </Flex>
                  );
                },
              )}
              <Button
                kind="tertiary"
                onClick={handleAddUrlMapping}
                size="md"
                startIcon="add-more"
              >
                Add URL mapping
              </Button>
            </Flex>

            <Flex gap="spaces-2" justifyContent="end">
              <Button kind="primary" onClick={handleSaveUrlMappings} size="md">
                Save
              </Button>
            </Flex>
          </Flex>
        </>
      ) : (
        <Flex flexDirection="column" gap="spaces-2">
          <>
            <Text kind="body-m">
              {createMessage(
                IN_APP_EMBED_SETTING.chromeExtensionBannerTitle,
                Boolean(chromeExtensionConfig.extensionId),
              )}
            </Text>
            <Flex alignItems="center" justifyContent="space-between">
              <Text color="var(--ads-v2-color-fg-muted)" kind="body-s">
                {createMessage(
                  IN_APP_EMBED_SETTING.chromeExtensionBannerDescription,
                )}
              </Text>

              <Button
                endIcon="share-box-line"
                href={
                  chromeExtensionConfig.extensionId
                    ? `chrome-extension://${chromeExtensionConfig.extensionId}/src/options/options.html`
                    : CHROME_EXTENSION_URL
                }
                kind="primary"
                onClick={(e) => {
                  if (chromeExtensionConfig.extensionId) {
                    e.preventDefault();

                    postMessage({
                      type: "OPEN_APPSMITH_AGENTS_EXTENSION",
                    });
                  }
                }}
                renderAs="a"
                target="_blank"
              >
                {createMessage(
                  IN_APP_EMBED_SETTING.chromeExtensionBannerButton,
                  Boolean(chromeExtensionConfig.extensionId),
                )}
              </Button>
            </Flex>
          </>
        </Flex>
      )}
    </Flex>
  );
}
